import React from 'react';
import Card from '@/components/Card/Card';
import CardBody from '@/components/Card/CardBody';
import GridContainer from '@/components/Grid/GridContainer';
import GridItem from '@/components/Grid/GridItem';
import CustomInput from '@/components/CustomInput/CustomInput';
import Button from '@/components/CustomButtons/Button';
import {
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { usePostHog } from 'posthog-js/react';
import ReportingColumnList from './ReportingColumnList';
import FieldErrorText, {
  fieldErrorHelperText,
} from '@/components/FieldErrorText/FieldErrorText';
import FormErrorSummary from '@/components/FormErrorSummary/FormErrorSummary';
import SftpDestinationList from './SftpDestinationList';

const labelStyle = {
  fontSize: '12px',
  color: 'grey',
  cursor: 'pointer',
};

const dateFormats = ['MMDDYYYY', 'YYYYMMDD', 'DDMMYYYY', 'YYYYDDMM'];

export default function OutboundReporting({
  formValues,
  setUpdatingBlocks,
  updatingBlocks,
  editId,
  isUpdating,
  onOutboundReportingCancel,
  updateOutboundReporting,
  availableColumns,
  availableLineItemColumns,
  handleInputChange,
  onSftpDestinationsChange,
  addReportingColumnGroup,
  updateReportingColumnGroup,
  removeReportingColumnGroup,
  sftpConnections,
  fieldErrors = {},
  getFieldErrors = () => [],
  generalErrors = [],
}) {
  const posthog = usePostHog();

  const isNewRefundReportingEnabled = posthog.isFeatureEnabled(
    'new-refund-reporting',
  );

  return (
    isNewRefundReportingEnabled && (
      <Card className='dashboardCard table_card_height'>
        <CardBody
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            height: '100%',
          }}>
          <span style={{ display: 'flex', alignItems: 'center' }}>
            <h3 className='subHeading'>Outbound Reporting</h3>
            <Switch
              name='outbound_reporting'
              checked={formValues.outbound_reporting}
              disabled={!isUpdating('outbound-reporting')}
              onChange={(value) =>
                handleInputChange({
                  target: {
                    name: 'outbound_reporting',
                    value: value.target.checked,
                  },
                })
              }
            />
          </span>
          <FormErrorSummary errors={generalErrors} />
          <FieldErrorText messages={getFieldErrors('outbound_reporting')} />
          <GridContainer>
            <GridItem xs={12} sm={12} md={6}>
              <FormControl
                component='fieldset'
                style={{ marginTop: 16 }}
                error={
                  !!fieldErrors.generate_acknowledgement_file ||
                  !!fieldErrors.generate_daily_report
                }>
                <FormLabel component='legend' style={labelStyle}>
                  Generate Files
                </FormLabel>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    marginTop: -20,
                  }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!formValues.generate_acknowledgement_file}
                        onChange={(e) =>
                          handleInputChange({
                            target: {
                              name: 'generate_acknowledgement_file',
                              value: e.target.checked,
                            },
                          })
                        }
                        name='generate_acknowledgement_file'
                        disabled={!isUpdating('outbound-reporting')}
                      />
                    }
                    label='Acknowledgement File'
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!formValues.generate_daily_report}
                        onChange={(e) =>
                          handleInputChange({
                            target: {
                              name: 'generate_daily_report',
                              value: e.target.checked,
                            },
                          })
                        }
                        name='generate_daily_report'
                        disabled={!isUpdating('outbound-reporting')}
                      />
                    }
                    label='Daily Report'
                  />
                </div>
                <FieldErrorText
                  messages={[
                    ...getFieldErrors('generate_acknowledgement_file'),
                    ...getFieldErrors('generate_daily_report'),
                  ]}
                />
              </FormControl>
            </GridItem>
          </GridContainer>
          <GridContainer>
            <GridItem xs={12} sm={12} md={6}>
              <div className='custom-select'>
                <FormControl
                  variant='standard'
                  error={!!fieldErrors.outbound_file_delimiter}>
                  <InputLabel id='outbound-file-delimiter-label'>
                    Outbound File Delimiter
                  </InputLabel>
                  <Select
                    disabled={!isUpdating('outbound-reporting')}
                    labelId='outbound-file-delimiter-label'
                    id='outbound-file-delimiter'
                    name='outbound_file_delimiter'
                    value={formValues.outbound_file_delimiter}
                    onChange={handleInputChange}
                    className='full-width-elem'>
                    <MenuItem value={'comma'}>Comma</MenuItem>
                    <MenuItem value={'pipe'}>Pipe</MenuItem>
                    <MenuItem value={'tab'}>Tab</MenuItem>
                  </Select>
                  <FieldErrorText
                    messages={getFieldErrors('outbound_file_delimiter')}
                  />
                </FormControl>
              </div>
            </GridItem>

            <GridItem xs={12} sm={12} md={6}>
              <CustomInput
                type='text'
                labelText='Outbound File Extension'
                disabled={!isUpdating('outbound-reporting')}
                onChange={handleInputChange}
                value={formValues.outbound_file_extension}
                name='outbound_file_extension'
                formControlProps={{
                  fullWidth: true,
                }}
                error={!!fieldErrors.outbound_file_extension}
                inputProps={{
                  helperText: fieldErrorHelperText(
                    getFieldErrors('outbound_file_extension'),
                  ),
                }}
              />
            </GridItem>

            <GridItem xs={12} sm={12} md={6}>
              <div className='custom-select'>
                <FormControl
                  variant='standard'
                  error={!!fieldErrors.outbound_file_date_format}>
                  <InputLabel id='outbound-file-date-format-label'>
                    Date Format that will be added to the end of the file name
                  </InputLabel>
                  <Select
                    disabled={!isUpdating('outbound-reporting')}
                    labelId='outbound-file-date-format-label'
                    id='outbound-file-date-format'
                    name='outbound_file_date_format'
                    value={formValues.outbound_file_date_format}
                    onChange={handleInputChange}
                    className='full-width-elem'>
                    {dateFormats.map((format) => (
                      <MenuItem key={format} value={format}>
                        {format}
                      </MenuItem>
                    ))}
                  </Select>
                  <FieldErrorText
                    messages={getFieldErrors('outbound_file_date_format')}
                  />
                </FormControl>
              </div>
            </GridItem>
            <GridItem xs={12} sm={12} md={6}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  minHeight: '90px',
                }}>
                <FormControl error={!!fieldErrors.encrypt_outbound}>
                  <FormLabel style={labelStyle} id='outbound-encryption-label'>
                    Is the file encrypted??
                  </FormLabel>
                  <RadioGroup
                    row
                    aria-labelledby='outbound-encryption-label'
                    name='encrypt_outbound'
                    value={formValues.encrypt_outbound}
                    onChange={handleInputChange}>
                    <FormControlLabel
                      value='true'
                      disabled={!isUpdating('outbound-reporting')}
                      control={<Radio />}
                      label='Yes'
                    />
                    <FormControlLabel
                      value='false'
                      disabled={!isUpdating('outbound-reporting')}
                      control={<Radio />}
                      label='No'
                    />
                  </RadioGroup>
                  <FieldErrorText
                    messages={getFieldErrors('encrypt_outbound')}
                  />
                </FormControl>
              </div>
            </GridItem>
            <GridItem xs={12} sm={12} md={6}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  minHeight: '90px',
                }}>
                <FormControl error={!!fieldErrors.include_headers_outbound}>
                  <FormLabel style={labelStyle} id='outbound-headers-label'>
                    Include headers in the file?
                  </FormLabel>
                  <RadioGroup
                    row
                    aria-labelledby='outbound-headers-label'
                    name='include_headers_outbound'
                    value={formValues.include_headers_outbound}
                    onChange={handleInputChange}>
                    <FormControlLabel
                      value='true'
                      disabled={!isUpdating('outbound-reporting')}
                      control={<Radio />}
                      label='Yes'
                    />
                    <FormControlLabel
                      value='false'
                      disabled={!isUpdating('outbound-reporting')}
                      control={<Radio />}
                      label='No'
                    />
                  </RadioGroup>
                  <FieldErrorText
                    messages={getFieldErrors('include_headers_outbound')}
                  />
                </FormControl>
              </div>
            </GridItem>
          </GridContainer>
          <SftpDestinationList
            destinations={formValues.sftp_destinations}
            sftpConnections={sftpConnections}
            isDisabled={!isUpdating('outbound-reporting')}
            generateAcknowledgementFile={
              !!formValues.generate_acknowledgement_file
            }
            generateDailyReport={!!formValues.generate_daily_report}
            fileExtension={formValues.outbound_file_extension}
            dateFormat={formValues.outbound_file_date_format}
            onChange={onSftpDestinationsChange}
            getFieldErrors={getFieldErrors}
          />
          {formValues.generate_acknowledgement_file && (
            <Accordion disabled={!isUpdating('outbound-reporting')}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography
                  color={
                    fieldErrors.acknowledgement_reporting_columns
                      ? 'error'
                      : undefined
                  }>
                  Acknowledgement Columns
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <FieldErrorText
                  messages={getFieldErrors('acknowledgement_reporting_columns')}
                />
                <ReportingColumnList
                  columns={formValues?.acknowledgement_reporting_columns}
                  onUpdateColumn={(idx, key, val) =>
                    updateReportingColumnGroup(
                      'acknowledgement_reporting_columns',
                      idx,
                      key,
                      val,
                    )
                  }
                  onRemoveColumn={(idx) =>
                    removeReportingColumnGroup(
                      'acknowledgement_reporting_columns',
                      idx,
                    )
                  }
                  onAddColumn={() =>
                    addReportingColumnGroup('acknowledgement_reporting_columns')
                  }
                  availableColumns={availableColumns}
                  isDisabled={!isUpdating('outbound-reporting')}
                  columnTypeLabel='Acknowledgement Column'
                  availableLineItemColumns={availableLineItemColumns}
                />
              </AccordionDetails>
            </Accordion>
          )}

          {formValues.generate_daily_report && (
            <Accordion disabled={!isUpdating('outbound-reporting')}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography
                  color={fieldErrors.reporting_columns ? 'error' : undefined}>
                  Reporting Columns
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <FieldErrorText
                  messages={getFieldErrors('reporting_columns')}
                />
                <ReportingColumnList
                  columns={formValues?.reporting_columns}
                  onUpdateColumn={(idx, key, val) =>
                    updateReportingColumnGroup(
                      'reporting_columns',
                      idx,
                      key,
                      val,
                    )
                  }
                  onRemoveColumn={(idx) =>
                    removeReportingColumnGroup('reporting_columns', idx)
                  }
                  onAddColumn={() =>
                    addReportingColumnGroup('reporting_columns')
                  }
                  availableColumns={availableColumns}
                  isDisabled={!isUpdating('outbound-reporting')}
                  columnTypeLabel='Reporting Column'
                  availableLineItemColumns={availableLineItemColumns}
                />
              </AccordionDetails>
            </Accordion>
          )}

          {editId && !updatingBlocks.includes('outbound-reporting') && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                marginTop: '20px',
              }}>
              <Button
                style={{ marginTop: '10px', marginBottom: '-10px' }}
                onClick={() =>
                  setUpdatingBlocks([...updatingBlocks, 'outbound-reporting'])
                }
                color='primary'>
                Edit
              </Button>
            </div>
          )}

          {updatingBlocks.includes('outbound-reporting') && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                marginTop: '20px',
              }}>
              <Button
                style={{ marginTop: '10px', marginBottom: '-10px' }}
                onClick={onOutboundReportingCancel}
                color='danger'>
                Cancel
              </Button>
              <Button
                style={{ marginTop: '10px', marginBottom: '-10px' }}
                onClick={updateOutboundReporting}
                color='primary'>
                Save Outbound Reporting
              </Button>
            </div>
          )}
        </CardBody>
      </Card>
    )
  );
}
